import { useEffect, useState } from "react";
import {
  Briefcase, Upload, CheckCircle2, Loader2, MapPin, Clock, Sparkles, GraduationCap, ArrowDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import AiText from "@/components/AiText";
import api, { formatApiError } from "@/lib/api";
import { CAREER_ICONS } from "@/lib/aiImages";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

const ACCEPT = ".pdf,.doc,.docx";
const MAX = 5 * 1024 * 1024;
const emptyForm = { full_name: "", email: "", phone: "", location: "", education: "", interest_area: "", skills: "", links: "", intro: "", duration: "", availability: "" };

export default function Careers() {
  const { t } = useLanguage();
  const areas = t("car.areas");
  const [positions, setPositions] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [fit, setFit] = useState("");
  const [fitLoading, setFitLoading] = useState(false);

  useEffect(() => {
    api.get("/careers/positions").then((r) => setPositions(r.data)).catch(() => setPositions([]));
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const scrollToForm = () => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });

  const pickArea = (area) => { set("interest_area", area); scrollToForm(); };

  const checkFit = async () => {
    const background = [form.education && `Education: ${form.education}`, form.skills && `Skills: ${form.skills}`, form.intro].filter(Boolean).join(". ");
    if (!form.interest_area.trim() || !background.trim()) { toast.error("Choose an area and add your education, skills or introduction first."); return; }
    setFitLoading(true);
    setFit("");
    try {
      const { data } = await api.post("/ai/career-fit", { position: form.interest_area, background });
      setFit(data.summary);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate a summary.");
    } finally {
      setFitLoading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(pdf|doc|docx)$/i.test(f.name)) { setErrors((er) => ({ ...er, resume: t("car.errResumeType") })); return; }
    if (f.size > MAX) { setErrors((er) => ({ ...er, resume: t("car.errResumeSize") })); return; }
    setErrors((er) => ({ ...er, resume: undefined }));
    setFile(f);
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = t("car.errName");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("car.errEmail");
    if (!form.interest_area.trim()) e.interest_area = t("car.errInterest");
    if (!file) e.resume = t("car.errResume");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("location", form.location);
      fd.append("education", form.education);
      fd.append("interest_area", form.interest_area);
      fd.append("position", form.interest_area);
      fd.append("skills", form.skills);
      fd.append("linkedin", form.links);
      fd.append("intro", form.intro);
      fd.append("duration", form.duration);
      fd.append("availability", form.availability);
      fd.append("resume", file);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success(t("car.successH"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]";
  const err = (k) => errors[k] && <p className="mt-1 text-xs text-red-400" data-testid={`apply-error-${k}`}>{errors[k]}</p>;

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden vora-hero-radial border-b border-white/8" data-testid="careers-hero">
        <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3.5 py-1.5">
              <Briefcase className="h-3.5 w-3.5 text-[#4F8CFF]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">{t("car.label")}</span>
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {t("car.h1a")} <span className="vora-text-gradient">{t("car.accent")}</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-lg text-slate-300 leading-relaxed">{t("car.intro")}</p>
            <Button onClick={scrollToForm} className="mt-8 h-12 px-7 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full text-base font-semibold group" data-testid="apply-internship-cta">
              {t("car.ctaApply")}
              <ArrowDown className="ml-2 h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ============ PAID INTERNSHIP BANNER ============ */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-[#4F8CFF]/30 bg-gradient-to-r from-[#4F8CFF]/12 via-[#7C5CFF]/10 to-transparent p-8 sm:p-10" data-testid="paid-internship-banner">
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#7C5CFF]/20 blur-3xl" aria-hidden />
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF] text-white vora-glow-blue">
                  <GraduationCap className="h-8 w-8" />
                </span>
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 vora-pulse-dot" />
                    {t("car.paidBadge")}
                  </span>
                  <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-white">{t("car.paidH2")}</h2>
                  <p className="mt-2 max-w-2xl text-base text-slate-300 leading-relaxed">{t("car.paidBody")}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ INTERNSHIP AREAS ============ */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>{t("car.areasLabel")}</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t("car.areasH2")}</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {areas.map((area, i) => {
              const selected = form.interest_area === area;
              return (
                <Reveal key={area} delay={(i % 3) * 0.05}>
                  <button
                    type="button"
                    onClick={() => pickArea(area)}
                    className={`area-card-3d w-full h-full text-left rounded-2xl p-5 flex items-center gap-4 ${
                      selected ? "bg-gradient-to-r from-[#4F8CFF]/20 to-[#7C5CFF]/12 border border-[#4F8CFF]/50" : "vora-card"
                    }`}
                    data-testid={`area-card-${i}`}
                  >
                    <img
                      src={CAREER_ICONS[i]}
                      alt=""
                      loading="lazy"
                      className="area-icon-3d h-14 w-14 shrink-0 rounded-2xl object-cover border border-white/10"
                      style={{ animationDelay: `${0.15 + i * 0.08}s` }}
                    />
                    <span className="text-sm font-medium text-white">{area}</span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ OPEN POSITIONS (admin-managed) ============ */}
      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>{t("car.openLabel")}</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight">{t("car.openH2")}</h2>
          </Reveal>
          {positions && positions.length === 0 && (
            <Reveal delay={0.05}>
              <div className="mt-8 vora-card rounded-2xl p-8 text-center" data-testid="no-positions">
                <p className="text-base text-slate-300">{t("car.openEmpty")}</p>
              </div>
            </Reveal>
          )}
          {positions && positions.length > 0 && (
            <div className="mt-8 space-y-4">
              {positions.map((p, i) => (
                <Reveal key={p.id || i} delay={(i % 3) * 0.05}>
                  <div className="vora-card rounded-2xl p-6" data-testid={`position-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-lg font-semibold text-white">{p.title}</p>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                          {p.department && <span>{p.department}</span>}
                          {p.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</span>}
                          {p.job_type && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.job_type}</span>}
                          {p.experience && <span>{p.experience}</span>}
                        </div>
                      </div>
                      <Button onClick={() => { set("interest_area", p.title); scrollToForm(); }} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid={`position-apply-${i}`}>{t("car.apply")}</Button>
                    </div>
                    {p.description && <p className="mt-3 text-sm text-slate-400">{p.description}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ APPLICATION FORM ============ */}
      <section id="apply-form" className="pb-24 scroll-mt-24">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          <Reveal>
            <div className="vora-glass rounded-3xl p-6 sm:p-9">
              <h2 className="text-2xl font-semibold text-white">{t("car.formH2")}</h2>
              <p className="mt-1 text-sm text-slate-400">{t("car.formSub")}</p>
              {done ? (
                <div className="text-center py-10" data-testid="careers-success">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400"><CheckCircle2 className="h-8 w-8" /></div>
                  <h3 className="mt-6 text-xl font-semibold text-white">{t("car.successH")}</h3>
                  <p className="mt-2 text-slate-400 max-w-md mx-auto">{t("car.successP")}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="careers-form" noValidate>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">{t("car.fName")} *</Label>
                      <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="apply-name" />
                      {err("full_name")}
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fEmail")} *</Label>
                      <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="apply-email" />
                      {err("email")}
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fPhone")}</Label>
                      <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="apply-phone" />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fLocation")}</Label>
                      <Input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} data-testid="apply-location" />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fEducation")}</Label>
                      <Input value={form.education} onChange={(e) => set("education", e.target.value)} placeholder={t("car.phEducation")} className={inputCls} data-testid="apply-education" />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fInterest")} *</Label>
                      <select
                        value={form.interest_area}
                        onChange={(e) => set("interest_area", e.target.value)}
                        className="mt-1.5 w-full rounded-md border border-white/12 bg-white/[0.03] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#4F8CFF] [&>option]:bg-[#0B1526]"
                        data-testid="apply-interest"
                      >
                        <option value="">{t("car.phSelect")}</option>
                        {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      {err("interest_area")}
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fDuration")}</Label>
                      <Input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder={t("car.phDuration")} className={inputCls} data-testid="apply-duration" />
                    </div>
                    <div>
                      <Label className="text-slate-300">{t("car.fAvailability")}</Label>
                      <Input value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder={t("car.phAvailability")} className={inputCls} data-testid="apply-availability" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">{t("car.fSkills")}</Label>
                    <Input value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder={t("car.phSkills")} className={inputCls} data-testid="apply-skills" />
                  </div>
                  <div>
                    <Label className="text-slate-300">{t("car.fLinks")}</Label>
                    <Input value={form.links} onChange={(e) => set("links", e.target.value)} className={inputCls} data-testid="apply-links" />
                  </div>
                  <div>
                    <Label className="text-slate-300">{t("car.fIntro")}</Label>
                    <Textarea value={form.intro} onChange={(e) => set("intro", e.target.value)} placeholder={t("car.phIntro")} className={`${inputCls} min-h-[110px]`} data-testid="apply-intro" />
                    <button type="button" onClick={checkFit} disabled={fitLoading} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#8fb4ff] hover:text-white transition-colors" data-testid="career-fit-btn">
                      {fitLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {t("car.fitBtn")}
                    </button>
                    {fit && (
                      <div className="mt-3 rounded-xl border border-[#4F8CFF]/20 bg-[#4F8CFF]/[0.06] p-4" data-testid="career-fit-result">
                        <p className="text-[11px] font-mono uppercase tracking-wider text-[#8fb4ff] mb-2">{t("car.fitLabel")}</p>
                        <AiText text={fit} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-300">{t("car.fResume")} *</Label>
                    <label className="mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-4 cursor-pointer hover:border-[#4F8CFF]/40 transition-colors" data-testid="apply-resume-label">
                      <Upload className="h-5 w-5 text-[#4F8CFF]" />
                      <span className="text-sm text-slate-300">{file ? file.name : t("car.resumePick")}</span>
                      <input type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid="apply-resume-input" />
                    </label>
                    {err("resume")}
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="apply-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("car.submit")}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
